import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { breadcrumbScript } from "@/lib/schema";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Terms & Conditions | Avolus Cars Vancouver" },
      {
        name: "description",
        content:
          "Avolus Cars rental terms: driver eligibility, deposit, mileage, insurance and LDW, delivery, cancellation, prohibited use and BC governing law.",
      },
      { property: "og:title", content: "Terms & Conditions | Avolus Cars" },
      {
        property: "og:description",
        content:
          "Rental terms for Avolus Cars: eligibility, deposits, mileage, insurance, delivery, cancellation and governing law.",
      },
      { property: "og:url", content: "https://avoluscars.com/terms" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/terms" }],
    scripts: [
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Terms & Conditions", url: "https://avoluscars.com/terms" },
      ]),
    ],
  }),
  component: Terms,
});

const EFFECTIVE = "September 14, 2026";

function Terms() {
  return (
    <Layout>
      <section className="pt-40 pb-12">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Legal</Eyebrow>
            <h1 className="font-display text-5xl md:text-6xl text-foreground">
              Terms &amp; Conditions
            </h1>
            <p className="mt-6 text-foreground/70">
              These terms govern use of avoluscars.com and rentals booked with Avolus Cars. Each
              rental is also subject to the signed rental agreement provided at the time of
              delivery; where the two differ, the signed rental agreement prevails.
            </p>
            <p className="mt-3 text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Effective {EFFECTIVE}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 space-y-10 text-sm leading-relaxed text-foreground/80">
          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">1. About us</h2>
            <p>
              Avolus Cars, 250 - 997 Seymour St #1711, Vancouver, BC V6B 3M1, Canada.
              Phone +1-778-379-8881, email info@avoluscars.com. We deliver vehicles across Metro
              Vancouver, YVR, Abbotsford and Whistler; we do not operate a public pickup counter.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">2. Driver eligibility</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Minimum age 25.</li>
              <li>
                A full, valid driver's licence held for at least two years. International renters
                must present a passport and, where the licence is not in English, an
                International Driving Permit.
              </li>
              <li>
                Personal auto insurance with valid coverage, or approved coverage arranged at
                booking.
              </li>
              <li>
                A major credit card in the renter's own name for the deposit. Only drivers listed
                on the rental agreement may operate the vehicle.
              </li>
            </ul>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">3. Rates, deposits and payment</h2>
            <p>
              Rates shown on this site are in Canadian dollars, per 24-hour period, before taxes
              and optional extras, and may change without notice until a booking is confirmed.
              Multi-day discounts apply automatically as shown on our{" "}
              <a href="/rates" className="text-foreground underline underline-offset-4">
                rates page
              </a>
              . A refundable $2,000 security deposit applies to every vehicle. It is authorised on
              the renter's credit card before delivery and released after the vehicle is returned
              and inspected. We accept Visa, Mastercard, American Express, PayPal, Interac
              e-Transfer, cash, Bitcoin and Ethereum.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">4. Mileage and extra charges</h2>
            <p>
              Each rental includes a daily kilometre allowance stated at booking. Distance beyond
              that allowance is billed at the per-kilometre rate published on our rates page.
              Additional charges may apply for fuel replacement, excessive cleaning, smoking in
              the vehicle, pet hair, toll and bridge fees, traffic or parking violations, and
              late return.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">5. Insurance and damage</h2>
            <p>
              The renter is responsible for the vehicle for the entire rental period, including
              loss, theft and damage. A Loss Damage Waiver applies to every rental at
              $60 per day and limits the renter's liability subject to its terms and deductible.
              The waiver does not apply where damage results from prohibited use, an unlisted
              driver, or impaired or reckless operation.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">6. Prohibited use</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Racing, track use, timed events, off-road or unpaved surfaces.</li>
              <li>Towing, pushing, or carrying loads beyond the vehicle's rating.</li>
              <li>Subletting, ridesharing, commercial transport, or driving instruction.</li>
              <li>Operation while impaired, or by anyone not listed on the rental agreement.</li>
              <li>Taking the vehicle outside British Columbia without prior written approval.</li>
            </ul>
            <p className="mt-3">
              Prohibited use voids the damage waiver and may end the rental immediately, with the
              vehicle recovered at the renter's expense.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">7. Delivery and collection</h2>
            <p>
              We deliver to and collect from an address you provide. Delivery windows are
              estimates and may be affected by traffic, weather or ferry schedules. Someone
              matching the listed driver must be present with their licence and credit card to
              receive the vehicle. Delivery outside our standard service area may carry a fee
              quoted before booking is confirmed.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">8. Cancellation and changes</h2>
            <p>
              Cancellation and amendment terms are shown during booking and confirmed in your
              reservation email. We may cancel or substitute a vehicle of equivalent or higher
              class where the booked vehicle becomes unavailable through damage, mechanical
              failure or late return by a prior renter; where no equivalent vehicle is available,
              amounts paid for that rental are refunded in full.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">9. Breakdown and accidents</h2>
            <p>
              Report any accident, theft or mechanical fault to us immediately at
              +1-778-379-8881, and to police where required by law. Do not authorise repairs
              without our written consent.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">10. Website content</h2>
            <p>
              Vehicle photographs, specifications and availability are provided in good faith and
              may vary by individual vehicle and model year. Site content, branding and imagery
              are owned by Avolus Cars and may not be reproduced without permission.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">11. Liability</h2>
            <p>
              To the extent permitted by law, Avolus Cars is not liable for indirect or
              consequential losses, including missed events, flights or connections, arising from
              a delayed, unavailable or substituted vehicle. Nothing in these terms limits
              liability that cannot be limited under British Columbia law.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">12. Governing law</h2>
            <p>
              These terms are governed by the laws of the Province of British Columbia and the
              federal laws of Canada that apply in it. Disputes are subject to the exclusive
              jurisdiction of the courts of British Columbia.
            </p>
          </Reveal>

          <Reveal>
            <p className="border-t border-border pt-8">
              Questions? Email{" "}
              <a href="mailto:info@avoluscars.com" className="text-foreground underline underline-offset-4">
                info@avoluscars.com
              </a>
              . See also our{" "}
              <a href="/privacy" className="text-foreground underline underline-offset-4">
                Privacy Policy
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
